import { commitMutation, graphql } from 'react-relay';
import type { IntlConfig } from 'react-intl';
import type { Locale as DateFnsLocaleData } from 'date-fns';
import type { Localization } from '@mui/material/locale';
import type { PickersLocaleText } from '@mui/x-date-pickers';
import { getRelayEnvironment } from '@/RelayEnvironment';

// We have not yet enabled saving user settings for PQi in the backend, so
// for now, this will be false.
const __USER_SETTINGS_DISABLED__ = true;

const availableLocales = ['en', 'sv'] as const;
const defaultLocale = availableLocales[0];

export type AppLocale = (typeof availableLocales)[number];
export type ChangeListener = (
    locale: AppLocale,
    localeData: LocaleData,
) => void;

export type LocaleData = {
    muiTheme?: Localization;
    muiXDatePickers?: Partial<PickersLocaleText<Date>>;
    dateFns?: DateFnsLocaleData;
    messages?: IntlConfig['messages'];
};
const changeLocaleListeners: Array<ChangeListener> = [];

const { localStorage, navigator } = window;

let currentLocale = determineLocale();
let currentLocaleData: LocaleData = {};

export async function bootstrapI18n(): Promise<AppLocale> {
    await loadLocaleData();
    invokeChangeLocaleListeners();
    changeLocaleListeners.push(saveLocale);
    return getLocale();
}

function isLocaleAvailable(locale: string | null) {
    return locale && availableLocales.indexOf(locale as AppLocale) > -1;
}

function determineLocale(): AppLocale {
    if (localStorage) {
        const localeFromLocalStorage = localStorage.getItem('locale');
        if (isLocaleAvailable(localeFromLocalStorage)) {
            return localeFromLocalStorage as AppLocale;
        }
    }

    if (navigator.language) {
        const languageFromBrowser = window.navigator.language.substring(0, 2);
        if (isLocaleAvailable(languageFromBrowser)) {
            return languageFromBrowser as AppLocale;
        }
    }
    return defaultLocale;
}

async function loadLocaleData(): Promise<void> {
    currentLocaleData = (await import(`./localeData/${currentLocale}.ts`))
        .default;
    // There are issues in safari, might be related to this one
    // https://github.com/facebook/react/issues/21809
    // But probably it is a bug in webpack or in safari itself
    // It seems to help to add a timeout, perhaps something is not executed?
    await new Promise((resolve) => setTimeout(resolve, 0));
}

function invokeChangeLocaleListeners() {
    changeLocaleListeners.forEach((listener) =>
        listener(currentLocale, currentLocaleData),
    );
}

export async function setLocale(locale: AppLocale) {
    if (!isLocaleAvailable(locale)) {
        setLocale(defaultLocale);
        return;
    }
    if (localStorage) {
        localStorage.setItem('locale', locale);
    }
    if (currentLocale === locale) {
        return;
    }
    currentLocale = locale;
    await loadLocaleData();
    invokeChangeLocaleListeners();
}

export function getLocale(): AppLocale {
    return currentLocale;
}

export function getLocaleData(): LocaleData {
    return currentLocaleData;
}

export function addChangeLocaleListener(listener: ChangeListener) {
    changeLocaleListeners.push(listener);
}

function saveLocale(locale: AppLocale) {
    if (__USER_SETTINGS_DISABLED__) {
        return;
    }
    commitMutation(getRelayEnvironment(), {
        mutation: graphql`
            mutation i18nChangeUserSettingsMutation(
                $input: ChangeUserSettingsInput!
            ) {
                changeUserSettings(input: $input) {
                    userSettings {
                        preferredLocale
                    }
                }
            }
        `,
        variables: {
            input: {
                preferredLocale: locale,
            },
        },
        onError: console.error,
    });
}
